<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, getCurrentInstance, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import _ from 'lodash'
import StreamService from '@/services/StreamService'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronUpDown from '@/components/icon/IconChevronUpDown.vue'
import IconClose from '@/components/icon/IconClose.vue'
import IconCheckInCircle from '@/components/icon/IconCheckInCircle.vue'
import IconChevronRight from '@/components/icon/IconChevronRight.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const store = useStore()

// Props
const props = defineProps({
	stream: {
		type: Object,
	},
	size: {
		type: String,
		default: 'md'
	},
	fullWidth: {
		type: Boolean,
		default: false,
	},
	showAlert: {
		type: Boolean,
		default: false,
	},
	selectorDisabled: {
		type: Boolean,
		default: false,
	},
	filled: {
		type: Boolean,
		default: false,
	}
})

// Emits
const emit = defineEmits(['switch-stream-region'])

// Refs
const visible = ref(false)
const regions = ref([])
const activeArea = ref('')
const newRegion = ref(null)
const widgetRef = ref(null)

// Computed
const resolvedRegion = computed(() => props.stream?.uiRegion || props.stream?.region || null)

const areas = computed(() => {
	const areaList = [...new Set(regions.value.map(item => item.area && item.area))].filter(item => item !== undefined)
	return areaList
})

const regionsFiltered = (area) => {
	const list = regions.value.filter(item => item.area === area && item.platforms.find(item => item === 'restream'))
	if (area === 'North America') {
		if (list.length > 1 && list[0].name === 'Mexico') {
    		const first = list.shift()
    		list.push(first) 
		}
	}
	return list
}

// Click outside handler
const handleClickOutside = (event) => {
	if (widgetRef.value && !widgetRef.value.contains(event.target)) {
		visible.value = false
	}
}

// Watch for visibility changes to add/remove click outside listener
watch(visible, async (newValue) => {
	if (newValue) {
		await nextTick()
		document.addEventListener('click', handleClickOutside)
	} else {
		document.removeEventListener('click', handleClickOutside)
	}
})

// Lifecycle
onMounted(async () => {
	// Get regions from store (already loaded at app level)
	await store.dispatch('Regions/getRegions', 'list') // Ensure loaded
	// Use getter and clone to prevent Vuex mutation errors
	regions.value = _.cloneDeep(store.getters['Regions/getRegionsByCategory']('list'))
	if (resolvedRegion.value?.area) {
		activeArea.value = resolvedRegion.value.area
	} else {
		activeArea.value = areas.value[0]
	}
})

onUnmounted(() => {
	document.removeEventListener('click', handleClickOutside)
})

// Methods
const toggleVisible = () => {
	visible.value = !visible.value
}
const getCountryFlag = (region) => {
	const identifier = _.get(region || {}, 'identifier', '');
	return `https://assets.castr.io/countryflags/${identifier}/flat/64.png`;
}
const close = () => {
	visible.value = false
}
const setActiveArea = (area) => {
	activeArea.value = area
}
const setRegion = (region, area) => {
	newRegion.value = region
	activeArea.value = area
	if (props.showAlert) {
		showModal('confirm-region-change')
	} else {
		emit('switch-stream-region', newRegion.value)
		visible.value = false
		newRegion.value = null
	}
}
const confirmRegionChange = () => {
	emit('switch-stream-region', newRegion.value)
	visible.value = false
	newRegion.value = null
}
const closeModal = () => {
	hideModal('confirm-region-change')
	visible.value = false
	newRegion.value = null
}
const isActiveRegion = (region) => region?._id === resolvedRegion.value?._id
const isActiveArea = (area) => area === activeArea.value
</script>

<template>
	<div class="w-full relative" ref="widgetRef">
		<FormButton v-if="filled" :disabled="selectorDisabled" @click.stop="toggleVisible"
			custom-classes="w-full flex items-center bg-surface-3 hover:bg-surface-4 rounded-lg px-3 h-10 text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
			<template v-if="resolvedRegion">
				<img :src="getCountryFlag(resolvedRegion)" width="16" class="region-flag mr-2 rounded-sm shrink-0">
				<span class="region-name text-surface-9 text-sm leading-none">{{ resolvedRegion.name }}</span>
			</template>
			<span v-else class="text-surface-7 text-sm leading-none">Choose your region</span>
			<IconBase class="ml-auto mr-0 text-surface-7">
				<IconChevronUpDown />
			</IconBase>
		</FormButton>
		<FormButton v-else type="secondary" :disabled="selectorDisabled" class="justify-start text-left w-full"
			@click.stop="toggleVisible" :size="size" :block="fullWidth">
			<div class="flex items-center" v-if="resolvedRegion">
				<img :src="getCountryFlag(resolvedRegion)" width="20" class="region-flag mr-2">
				<span class="region-name">{{ resolvedRegion.name }}</span>
			</div>
			<div v-else class="font-normal text-left w-full">
				Choose your region
			</div>
			<IconBase class="ml-auto mr-0">
				<IconChevronUpDown />
			</IconBase>
		</FormButton>
		<div v-if="visible" class="absolute top-9 left-0 z-40 w-full bg-surface-2 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.35)] overflow-hidden">
			<div class="flex py-2 items-center">
				<h4 class="text-surface-7 ml-3 text-sm m-0">{{ resolvedRegion ? 'Change your region' : 'Select a region' }}</h4>
				<FormButton type="link" is-icon size="sm" class="ml-auto p-0 mr-2" @click.stop="close">
					<IconBase>
						<IconClose />
					</IconBase>
				</FormButton>
			</div>
			<div class="flex max-h-[320px]">
				<ul class="w-[42%] shrink-0 overflow-y-auto p-1 bg-surface-1 list-none m-0">
					<li v-for="(area, index) in areas" :key="index">
						<FormButton :custom-classes="'w-full flex items-center justify-between text-left text-sm px-3 py-2 rounded-md transition-colors ' + (isActiveArea(area) ? 'bg-surface-3 text-white' : 'text-surface-8 hover:bg-surface-2 hover:text-white')"
							@click.stop="setActiveArea(area)">
							<span class="truncate">{{ area }}</span>
							<IconBase width="14" height="14" class="shrink-0 opacity-60">
								<IconChevronRight />
							</IconBase>
						</FormButton>
					</li>
				</ul>
				<ul class="grow overflow-y-auto p-1 list-none m-0">
					<li v-for="(region, index) in regionsFiltered(activeArea)" :key="index">
						<FormButton :custom-classes="'w-full flex items-center text-left text-sm px-3 py-2 rounded-md hover:bg-surface-3 transition-colors ' + (isActiveRegion(region) ? 'bg-surface-3 text-white' : 'text-surface-9')"
							@click.stop="setRegion(region, activeArea)">
							<img width="22" class="mr-2 shrink-0 rounded-sm" :src="getCountryFlag(region)" :alt="region.name + ' flag'">
							<span class="truncate">{{ region.name }}</span>
							<IconBase v-if="isActiveRegion(region)" class="ml-auto mr-0 text-success shrink-0">
								<IconCheckInCircle />
							</IconBase>
						</FormButton>
					</li>
				</ul>
			</div>
		</div>
		<confirm-modal modalId="confirm-region-change" message="Change region"
			body="Changing the region will alter your RTMP/SRT URL. Please ensure to update the encoder accordingly."
			ok-text="Confirm" cancel-text="Cancel" @modal-cancel="closeModal" @modal-confirm="confirmRegionChange" />
	</div>
</template>